import { useApolloClient } from '@apollo/client/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { useT } from '../../../translation/src';
import { ProjectAccessLevel, User, UsersDocument, useProjectAddGuestMutation, useProjectRemoveGuestMutation, useProjectSetGuestAccessLevelMutation } from '../../../types/graphqlTypes';
import { ProjectAccessPicker, ProjectAccessSelection } from '../ProjectAccessPicker';

const useTranslations = () => ({
  manageAccess: useT('manage access', { swc: true }),
  save: useT('save', { swc: true }),
  cancel: useT('cancel', { swc: true }),
});

interface ProjectAccessModalProps {
  onClose: () => void;
  open?: boolean;
  user?: Pick<User, 'id' | 'name' | 'email'> & { projectAccess: Array<{ projectId: string; level: ProjectAccessLevel }> };
}

const toSelection = (projectAccess: Array<{ projectId: string; level: ProjectAccessLevel }> = []): Map<string, ProjectAccessLevel> => new Map(projectAccess.map(({ projectId, level }) => [projectId, level]));

/**
 * Which projects a guest may open and at what level. Saved as a diff against what the guest has
 * now: one add, remove or level change per changed project, untouched projects are never rewritten.
 */
const ProjectAccessModal_: React.FC2<ProjectAccessModalProps> = ({ onClose, open, user }) => {
  const translations = useTranslations();
  const { organisationId = '' } = useParams();
  const apolloClient = useApolloClient();
  const [addProjectGuest] = useProjectAddGuestMutation();
  const [removeProjectGuest] = useProjectRemoveGuestMutation();
  const [setGuestAccessLevel] = useProjectSetGuestAccessLevelMutation();

  const initial = useMemo(() => toSelection(user?.projectAccess), [user?.projectAccess]);
  const [selection, setSelection] = useState<ProjectAccessSelection>(initial);
  const [saving, setSaving] = useState(false);

  // Each opening starts from what the guest has right now (the row may have been refetched since).
  // State adjusted during render on the prop change, the React-endorsed alternative to an effect.
  const [openedFor, setOpenedFor] = useState(open);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) setSelection(initial);
  }

  const changes = useMemo(() => {
    const added = [...selection].filter(([projectId]) => !initial.has(projectId));
    const removed = [...initial.keys()].filter((projectId) => !selection.has(projectId));
    const relevelled = [...selection].filter(([projectId, level]) => initial.has(projectId) && initial.get(projectId) !== level);
    return { added, removed, relevelled, count: added.length + removed.length + relevelled.length };
  }, [selection, initial]);

  const onSave = useCallback(async () => {
    if (!user || !changes.count) return;
    setSaving(true);
    try {
      await Promise.all([
        ...changes.removed.map((projectId) => removeProjectGuest({ variables: { projectId, userId: user.id } })),
        ...changes.added.map(([projectId, level]) => addProjectGuest({ variables: { projectId, email: user.email, level } })),
        ...changes.relevelled.map(([projectId, level]) => setGuestAccessLevel({ variables: { projectId, userId: user.id, level } })),
      ]);
      await apolloClient.refetchQueries({ include: [UsersDocument] });
      onClose();
    } finally {
      setSaving(false);
    }
  }, [user, changes, removeProjectGuest, addProjectGuest, setGuestAccessLevel, apolloClient, onClose]);

  if (!user) return null;

  return (
    <Modal.Container onClose={onClose} open={open} panelClassName="w-full max-w-3xl">
      <Modal.Header onClose={onClose} className="!mb-4">
        <span className="flex flex-col">
          <span>{translations.manageAccess}</span>
          <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
            {user.name} · {user.email}
          </span>
        </span>
      </Modal.Header>
      <Modal.Body>
        <ProjectAccessPicker organisationId={organisationId} selection={selection} onChange={setSelection} active={!!open} />
      </Modal.Body>
      <Modal.Footer className="justify-end gap-3">
        <Button variant="secondary" size="xs" onClick={onClose} disabled={saving}>
          <span className="whitespace-nowrap">{translations.cancel}</span>
        </Button>
        <Button variant="primary" size="xs" onClick={onSave} loading={saving} disabled={!changes.count}>
          <span className="whitespace-nowrap">
            {translations.save}
            {changes.count > 0 && ` (${changes.count})`}
          </span>
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
};

export const ProjectAccessModal = memo(ProjectAccessModal_);
